// Writing a dish the catalog has never heard of.
//
// It used to be written in the row itself: picking "Custom dish" handed the
// eight columns over to be typed into. Eight boxes on one line is not enough
// room to ask eight questions in — the macros had no labels beyond their
// column heading, the unit and its weight shared one cell, and there was
// nowhere to say what any of it added up to. So it is a dialog now, with the
// questions laid out and the answer drawn beside them.
//
// Nothing here is stored anywhere but the row it fills in. A custom dish is
// one plan's own — that is what the badge at the top says, and why the dialog
// asks for everything every time.

import React, { useMemo, useState } from "react";
import {
  Button,
  DropdownField,
  DropdownOption,
  Modal,
  TextField,
  controlBoxClasses,
  fieldLabelClasses,
} from "../../../components/ui";
import { useFoodUnitCatalog } from "../hooks/useFoodCatalog";
import { MACRO_COLORS } from "../macroColors";
import {
  formatAmount,
  formatMacro,
  isNumberDraft,
  numberDraft,
  scaleMacros,
  servingMacros,
} from "../planMapping";
import { MACRO_COLUMNS } from "./optionTable";
import type { DishEntry, Macros } from "../types/plans.types";

/**
 * What a container's volume is counted in. Four rather than the catalog's own
 * list: this is the measure underneath the container, and every container in
 * the world is poured or weighed in one of these.
 */
const MEASURE_OPTIONS = ["gm", "ml", "oz", "fl oz"];

/** What a volume is read in until one is picked — nearly everything is weighed. */
const DEFAULT_MEASURE = "gm";

/**
 * The four boxes, in the order the design asks them: calories first, then the
 * three that make it up. Not `MACRO_COLUMNS` — that is the order the table's
 * columns are read in, and the two differ on purpose (carbs lead there, protein
 * leads here).
 */
const MACRO_FIELDS: Array<{
  key: keyof Macros;
  label: string;
  placeholder: string;
}> = [
  { key: "calories", label: "Calories (kcal)", placeholder: "eg. 24" },
  { key: "protein", label: "Protein (g)", placeholder: "eg. 24" },
  { key: "carbs", label: "Carbs (g)", placeholder: "eg. 12" },
  { key: "fat", label: "Fat (g)", placeholder: "eg. 18" },
];

/** The macros written out, where there is room for more than a column heading. */
const PREVIEW_LABELS: Record<keyof Macros, string> = {
  calories: "Cals",
  carbs: "Carbs",
  protein: "Protein",
  fat: "Fat",
};

/** The four boxes as they are typed — text, until they are read as numbers. */
type MacroDrafts = Record<keyof Macros, string>;

/**
 * One of the dialog's typed figures: the container's volume, the four macros,
 * and the quantity consumed.
 *
 * Nothing but a number can be typed into one — see `isNumberDraft`. Anything
 * else is simply not taken, so the box keeps what it had rather than flashing
 * a value it is about to reject. What is held is the text rather than the
 * number behind it, which is what lets a figure be half typed: `1.` on its way
 * to `1.5`, and an emptied box that stays empty instead of showing a `0`.
 */
const NumberField: React.FC<{
  label: string;
  value: string;
  onChange: (text: string) => void;
  placeholder: string;
  /**
   * Printed inside the box after the figure, and never part of it — what the
   * volume is measured in, and what the quantity is counted in.
   */
  suffix?: string;
  className?: string;
}> = ({ label, value, onChange, placeholder, suffix, className = "" }) => {
  const fieldId = React.useId();

  return (
    <div className={className}>
      <label htmlFor={fieldId} className={`${fieldLabelClasses} mb-1.5`}>
        {label}
      </label>
      {/* The form kit's own box, drawn around the input and its measure
          together — so this field sits at exactly the height the selects
          beside it do. The border follows the caret, which is why the box
          lights up on `focus-within`. */}
      <div className={controlBoxClasses()}>
        <input
          id={fieldId}
          type="text"
          // Text rather than `number`, which brings spinners and a wheel that
          // changes the value under a scrolling page. `decimal` still asks a
          // phone for the numeric keypad.
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          onChange={(event) => {
            if (isNumberDraft(event.target.value)) onChange(event.target.value);
          }}
          className="min-w-0 flex-1 bg-transparent text-inherit placeholder:text-ink-subtle focus:outline-none"
        />
        {suffix && (
          // Read off the input's own label rather than announced twice.
          <span
            aria-hidden="true"
            className="flex-shrink-0 truncate text-ink-subtle"
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};

export interface CustomDishModalProps {
  /**
   * The row the dialog is filling in. A fresh one carries nothing but the name
   * typed into the Dish field before its list let the search down; a custom
   * row being modified opens on everything it was written with.
   */
  dish: DishEntry;
  onClose: () => void;
  /**
   * The row as the dialog leaves it — everything about it, so whatever the
   * row held as a catalog pick is written over rather than left behind.
   * Closing is the caller's, the way saving is.
   */
  onSave: (patch: Partial<DishEntry>) => void;
}

/**
 * The dialog behind the Dish field's "Custom dish" row.
 *
 * Two halves: the questions on the left, and what they come to on the right.
 * The macros are asked for one serving — one container of the food — because
 * that is how a label reads them, and the panel multiplies them by the
 * quantity consumed. It does that live rather than on save, so the figure
 * being aimed at is on screen while it is being aimed at.
 */
const CustomDishModal: React.FC<CustomDishModalProps> = ({
  dish,
  onClose,
  onSave,
}) => {
  // The whole unit list rather than one food's: there is no food here to ask
  // about. Fetched once for the tab, so opening this twice costs one call.
  const containers = useFoodUnitCatalog();

  const [name, setName] = useState(dish.dish);
  const [measure, setMeasure] = useState(dish.unitMeasure ?? "");
  // Keyed on the id where the container was picked off the list, and on the
  // name for one typed in — `creatable` shows either back.
  const [container, setContainer] = useState(
    dish.unitId != null ? String(dish.unitId) : dish.unit
  );
  const [volume, setVolume] = useState(numberDraft(dish.unitGrams));
  const [quantity, setQuantity] = useState(numberDraft(dish.quantity));
  const [macros, setMacros] = useState<MacroDrafts>(() => {
    // A row this dialog wrote carries the serving it was written in; one read
    // back off a saved plan has it divided out of the total — see
    // `servingMacros`. Either way the boxes open on what was typed.
    const serving = servingMacros(dish);
    return {
      calories: numberDraft(serving.calories),
      carbs: numberDraft(serving.carbs),
      protein: numberDraft(serving.protein),
      fat: numberDraft(serving.fat),
    };
  });

  const containerOptions: DropdownOption[] = useMemo(
    () =>
      containers.options.map((unit) => ({
        value: String(unit.id),
        label: unit.name,
      })),
    [containers.options]
  );

  const picked = containers.options.find(
    (unit) => String(unit.id) === container
  );
  // What the row will be measured in: the container's own name, or whatever
  // was typed where the list had nothing to offer.
  const containerName = (picked?.name ?? container).trim();
  const measureLabel = measure || DEFAULT_MEASURE;

  const quantityNumber = Number(quantity) || 0;
  const volumeNumber = Number(volume) || 0;

  const serving: Macros = {
    calories: Number(macros.calories) || 0,
    carbs: Number(macros.carbs) || 0,
    protein: Number(macros.protein) || 0,
    fat: Number(macros.fat) || 0,
  };

  // What the panel multiplies by. One until a quantity is typed, so the panel
  // shows the serving that has been described rather than four zeros — and it
  // prints this figure beside the container, so it never claims a quantity
  // that was not given. Adding the dish needs a real one all the same.
  const servings = quantityNumber || 1;

  const total = scaleMacros(serving, servings);

  /** "2 Pieces (medium) · 300 gm" — what is being eaten, and what it comes to. */
  const servingLine = [
    containerName ? `${formatAmount(servings)} ${containerName}` : "",
    volumeNumber ? `${formatAmount(volumeNumber * servings)} ${measureLabel}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  /** "Katori - 150gm", or the bare name until it has been measured. */
  const unitLabel = volumeNumber
    ? `${containerName} - ${formatAmount(volumeNumber)}${measureLabel}`
    : containerName;

  // What the row cannot be stored without: a name, something to measure it in,
  // and some of it to eat — see `toItemPayload`. The macros are not among
  // them: a dish nobody has the figures for is still a line on the plan.
  const complete = !!name.trim() && !!containerName && quantityNumber > 0;

  const handleSave = () => {
    if (!complete) return;

    onSave({
      custom: true,
      dish: name.trim(),
      // Nothing the catalog said about the food this row used to be survives.
      foodId: null,
      unit: containerName,
      unitId: picked?.id ?? null,
      unitLabel,
      unitGrams: volumeNumber || null,
      unitMeasure: measureLabel,
      quantity: quantityNumber,
      // The row is worth what a serving is worth, times the servings eaten;
      // the serving itself is kept beside it so this dialog reopens on the
      // figures that were typed rather than on the product of them.
      macros: total,
      unitMacros: serving,
    });
  };

  // Whether this is a row being changed rather than one being written: the
  // dialog is the same either way, only its two verbs differ.
  const editing = !!dish.custom;

  return (
    <Modal
      open
      title="Create custom dish"
      // Drawn below rather than in the bar, so the badge and the line
      // explaining it can stand with the heading instead of under a rule.
      hideTitle
      onClose={onClose}
      className="max-w-[930px]"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button size="md" disabled={!complete} onClick={handleSave}>
            {editing ? "Update dish" : "Add dish"}
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-3">
        {/* The dialog is already named to a screen reader by the heading the
            bar would have carried, so this copy of it is the eye's alone. */}
        <span
          aria-hidden="true"
          className="text-[17px] 3xl:text-[19px] font-medium text-ink"
        >
          Create custom dish
        </span>
        <span className="flex-shrink-0 rounded-full border border-[#F0A64A] px-2.5 py-0.5 text-[11px] 3xl:text-[12px] text-[#E08600]">
          One time use only
        </span>
      </div>
      <p className="mt-1 text-[12px] 3xl:text-[13px] text-ink-muted">
        Won&rsquo;t be saved - a temporary dish for this plan only.
      </p>

      {/* Full width, so the rule separates the heading from the questions the
          way the bar's own would have. */}
      <div className="-mx-6 mt-4 border-t border-line" />

      <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex flex-col gap-4">
          <TextField
            label="Dish Name"
            value={name}
            onValueChange={setName}
            placeholder="eg. Scrambled Eggs"
          />

          {/* What the food is served in, and what one of that comes to. Three
              answers to one question, which is why they share a row. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,120px)_minmax(0,1fr)_minmax(0,150px)]">
            <DropdownField
              label="Unit"
              value={measure}
              onValueChange={setMeasure}
              options={MEASURE_OPTIONS}
              placeholder="Select"
            />
            <DropdownField
              label="Container"
              value={container}
              onValueChange={setContainer}
              options={containerOptions}
              // The list is the catalog's, and what is not on it is still
              // something somebody serves food in.
              searchable
              creatable
              placeholder={
                containers.loading ? "Loading containers…" : "Select container"
              }
            />
            <NumberField
              label="Container volume"
              value={volume}
              onChange={setVolume}
              placeholder="150"
              // Whatever the unit beside it says, so a glass of milk is
              // counted in millilitres and a katori of dal in grams.
              suffix={measureLabel}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {MACRO_FIELDS.map((field) => (
              <NumberField
                key={field.key}
                label={field.label}
                value={macros[field.key]}
                // The other three stand: a figure is written into the box it
                // belongs to, not into a fresh set of four.
                onChange={(text) =>
                  setMacros((current) => ({ ...current, [field.key]: text }))
                }
                placeholder={field.placeholder}
              />
            ))}
          </div>

          {/* Marked down its left edge: everything above describes the dish,
              this one says how much of it is eaten — and it is the figure the
              panel opposite multiplies by. */}
          <div className="flex items-stretch gap-3">
            <span
              aria-hidden="true"
              className="w-[3px] flex-shrink-0 rounded-full bg-brand"
            />
            <NumberField
              className="w-full max-w-[280px]"
              label="Quantity consumed"
              value={quantity}
              onChange={setQuantity}
              placeholder="eg. 2"
              // The container it is counted in, so the box reads "2 cup"
              // rather than a number with nothing behind it.
              suffix={containerName || undefined}
            />
          </div>
        </div>

        {/* The dish as the plan will hold it.
            As tall as the questions beside it — it is the answer to all of
            them, so it stands against the whole column rather than sitting at
            the top of it. */}
        <div className="flex h-full flex-col rounded-md border-[0.5px] border-[#E2E2E2] bg-[#FCFCFD] p-4">
          <p
            className={`truncate text-[15px] 3xl:text-[16px] font-medium ${
              name.trim() ? "text-ink" : "text-ink-subtle"
            }`}
            title={name.trim() || undefined}
          >
            {name.trim() || "Your dish"}
          </p>
          <p className="mt-0.5 truncate text-[12px] 3xl:text-[13px] text-ink-subtle">
            {servingLine || "Not measured yet"}
          </p>

          <div className="mt-4 flex flex-col gap-3">
            {MACRO_COLUMNS.map((macro) => (
              <div
                key={macro.key}
                className="flex items-center justify-between gap-3"
              >
                <span className="inline-flex items-center gap-2 text-[13px] 3xl:text-[14px] text-ink-muted">
                  <span
                    aria-hidden="true"
                    className="h-3.5 w-[3px] rounded-full"
                    style={{ backgroundColor: MACRO_COLORS[macro.key] }}
                  />
                  {PREVIEW_LABELS[macro.key]}
                </span>
                <span className="text-[14px] 3xl:text-[15px] text-ink">
                  {formatMacro(total[macro.key])}
                </span>
              </div>
            ))}
          </div>

          {/* Pushed to the foot of the panel, so it reads as a footnote to the
              figures above it however tall the column happens to be. */}
          <p className="mt-auto pt-6 text-[11px] 3xl:text-[12px] leading-[1.6] text-ink-muted">
            <span className="font-medium text-ink">Note:</span> Every value
            above is the one-serving figure multiplied by the consumed quantity.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default CustomDishModal;
